import { useGetIdentity, useGetList } from "ra-core";
import { useMemo } from "react";
import { Link } from "react-router";
import { AlertTriangle, Clock, Eye, Flame, Info } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  LeadScoreBadge,
  computeLeadScore,
  useOpenDealContactIds,
} from "../contacts/leadScore";
import {
  useConfigurationContext,
  useSignals,
} from "../root/ConfigurationContext";
import type { Contact, Deal, Task } from "../types";

/**
 * Keep an eye on - the intelligence strip: hot opportunities heating up,
 * deals going quiet, and follow-ups past due. Rule-based, no black box:
 * every threshold lives in Settings → Keep an eye on, and the info popover
 * says exactly how each item earned its spot.
 */

const daysAgo = (iso?: string) =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400_000) : 0;

export const SignalsCard = () => {
  const { identity } = useGetIdentity();
  const { scoringRules, currency } = useConfigurationContext();
  const signals = useSignals();
  const openDealIds = useOpenDealContactIds();

  const { data: contacts = [] } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 500 },
      filter: { sales_id: identity?.id },
    },
    { enabled: Number.isInteger(identity?.id) },
  );
  const { data: deals = [] } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
  });
  const { data: tasks = [] } = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "due_date", order: "ASC" },
      filter: { sales_id: identity?.id },
    },
    { enabled: Number.isInteger(identity?.id) },
  );

  const heatingUp = useMemo(
    () =>
      signals.heatingUp.enabled
        ? contacts
            .map((c) => ({
              contact: c,
              score: computeLeadScore(c, scoringRules ?? [], openDealIds),
            }))
            .filter(
              (r) =>
                r.score.score >= signals.heatingUp.minScore &&
                daysAgo(r.contact.last_seen) <= signals.heatingUp.activeDays,
            )
            .sort((a, b) => b.score.score - a.score.score)
            .slice(0, signals.perColumn)
        : [],
    [contacts, scoringRules, openDealIds, signals],
  );

  const slipping = useMemo(
    () =>
      signals.slipping.enabled
        ? deals
            .filter(
              (d) =>
                d.stage !== "won" &&
                d.stage !== "lost" &&
                !d.archived_at &&
                d.amount >= signals.slipping.minAmount &&
                daysAgo(d.updated_at) >= signals.slipping.quietDays,
            )
            .sort(
              (a, b) =>
                daysAgo(b.updated_at) - daysAgo(a.updated_at) ||
                b.amount - a.amount,
            )
            .slice(0, signals.perColumn)
        : [],
    [deals, signals],
  );

  const overdue = useMemo(
    () =>
      signals.overdue.enabled
        ? tasks
            .filter(
              (t) =>
                !t.done_date &&
                daysAgo(t.due_date) > signals.overdue.graceDays,
            )
            .slice(0, signals.perColumn)
        : [],
    [tasks, signals],
  );

  const fmt = (n: number) =>
    n.toLocaleString("en-US", {
      notation: "compact",
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 1,
    });

  const enabledColumns = [
    signals.heatingUp.enabled,
    signals.slipping.enabled,
    signals.overdue.enabled,
  ].filter(Boolean).length;
  if (enabledColumns === 0) return null;
  if (heatingUp.length === 0 && slipping.length === 0 && overdue.length === 0)
    return null;

  return (
    <Card>
      <CardContent className="px-4 py-3 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Keep an eye on</h2>
          <Popover>
            <PopoverTrigger
              aria-label="How these are picked"
              className="text-muted-foreground hover:text-foreground"
            >
              <Info className="w-3.5 h-3.5" />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 text-xs flex flex-col gap-2">
              <p className="font-medium text-sm">How these are picked</p>
              <p>
                <span className="font-medium">Heating up</span>: your contacts
                with a lead score of {signals.heatingUp.minScore}+ who were
                active in the last {signals.heatingUp.activeDays} days, best
                score first. The score comes from the{" "}
                <Link to="/marketing/workflows" className="underline hover:no-underline">
                  lead scoring rules
                </Link>{" "}
                - hover a badge to see which signals earned the points.
              </p>
              <p>
                <span className="font-medium">Slipping deals</span>: open deals
                quiet for {signals.slipping.quietDays}+ days
                {signals.slipping.minAmount > 0
                  ? ` (over ${fmt(signals.slipping.minAmount)})`
                  : ""}
                , longest quiet first.
              </p>
              <p>
                <span className="font-medium">Follow-ups overdue</span>: your
                open tasks past due
                {signals.overdue.graceDays > 0
                  ? ` by more than ${signals.overdue.graceDays} days`
                  : ""}
                .
              </p>
              <p className="text-muted-foreground border-t border-border pt-2">
                Tune every threshold in{" "}
                <Link to="/settings/signals" className="underline hover:no-underline">
                  Settings → Keep an eye on
                </Link>
                .
              </p>
            </PopoverContent>
          </Popover>
          <Link
            to="/settings/signals"
            className="ml-auto text-[11px] text-muted-foreground hover:text-foreground underline hover:no-underline"
          >
            Tune
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          {signals.heatingUp.enabled && (
            <div className="flex flex-col gap-1.5">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Flame className="w-3.5 h-3.5" /> Heating up
              </p>
              {heatingUp.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No contact is over {signals.heatingUp.minScore} points and
                  recently active.
                </p>
              )}
              {heatingUp.map(({ contact, score }) => (
                <Link
                  key={contact.id}
                  to={`/contacts/${contact.id}/show`}
                  className="flex items-center gap-2 hover:underline"
                >
                  <span className="truncate">
                    {contact.first_name} {contact.last_name}
                  </span>
                  <LeadScoreBadge score={score} size="sm" />
                </Link>
              ))}
            </div>
          )}

          {signals.slipping.enabled && (
            <div className="flex flex-col gap-1.5">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Slipping deals
              </p>
              {slipping.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Every open deal touched in the last{" "}
                  {signals.slipping.quietDays} days.
                </p>
              )}
              {slipping.map((d) => (
                <Link
                  key={d.id}
                  to={`/deals/${d.id}/show`}
                  className="hover:underline"
                >
                  <span className="truncate block">{d.name}</span>
                  <span className="text-xs text-[#c97a63]">
                    {fmt(d.amount)} · quiet {daysAgo(d.updated_at)} days
                  </span>
                </Link>
              ))}
            </div>
          )}

          {signals.overdue.enabled && (
            <div className="flex flex-col gap-1.5">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Follow-ups overdue
              </p>
              {overdue.length === 0 && (
                <p className="text-xs text-muted-foreground">All caught up.</p>
              )}
              {overdue.map((t) => (
                <Link
                  key={t.id}
                  to={`/contacts/${t.contact_id}/show`}
                  className="hover:underline"
                >
                  <span className="truncate block">{t.text}</span>
                  <span className="text-xs text-[#c97a63]">
                    due {daysAgo(t.due_date)} days ago
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
